import { useState } from 'react'
import type { Procedure } from '../../store/treatmentsSlice'

interface ProceduresProps {
  procedures: Procedure[]
  onAdd: (data: { treatment_id: string; name: string; description: string }) => Promise<void>
  onToggleStatus: (id: string, newStatus: string) => Promise<void>
  treatmentId: string
}

function getStatusStyle(status: string): string {
  switch (status) {
    case 'Done':
      return 'bg-accent-subtle-bg text-status-success'
    case 'Pending':
      return 'bg-[#f0e8d0] text-status-warning'
    default:
      return 'bg-border text-text-muted'
  }
}

export function Procedures({ procedures, onAdd, onToggleStatus, treatmentId }: ProceduresProps) {
  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')

  const handleSaveNew = async () => {
    if (!newName.trim()) return
    await onAdd({ treatment_id: treatmentId, name: newName.trim(), description: newDescription.trim() })
    setAdding(false)
    setNewName('')
    setNewDescription('')
  }

  return (
    <div className="bg-surface rounded-[6px] border border-border-light" data-testid="procedures-section">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-light">
        <h2 className="text-[14px] font-semibold text-text-primary">Procedures</h2>
        <button
          data-testid="add-procedure-button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
        >
          Add Procedure
        </button>
      </div>

      <div className="p-4 max-sm:p-3">
        {procedures.length === 0 && !adding && (
          <p className="text-[13px] text-text-muted" data-testid="procedures-empty">
            No procedures added yet
          </p>
        )}

        <div className="flex flex-col gap-2">
          {procedures.map((proc) => (
            <div
              key={proc.id}
              className="flex items-center gap-3 py-2 px-3 rounded-[4px] hover:bg-hover"
              data-testid="procedure-item"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-medium text-text-primary" data-testid="procedure-name">
                    {proc.name}
                  </span>
                  <span
                    data-testid="procedure-status-badge"
                    className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${getStatusStyle(proc.status)}`}
                  >
                    {proc.status}
                  </span>
                </div>
                {proc.description && (
                  <p className="text-[12px] text-text-muted mt-0.5" data-testid="procedure-description">
                    {proc.description}
                  </p>
                )}
              </div>
              <button
                data-testid="procedure-toggle-status"
                onClick={() => onToggleStatus(proc.id, proc.status === 'Pending' ? 'Done' : 'Pending')}
                className={`inline-flex items-center h-6 px-2 rounded-[3px] text-[11px] font-medium cursor-pointer transition-colors duration-100 ${
                  proc.status === 'Pending'
                    ? 'bg-accent-subtle-bg text-accent hover:bg-accent hover:text-white'
                    : 'bg-[#f0e8d0] text-status-warning hover:bg-status-warning hover:text-white'
                }`}
              >
                {proc.status === 'Pending' ? 'Mark Done' : 'Mark Pending'}
              </button>
            </div>
          ))}

          {adding && (
            <div className="flex flex-col gap-2 py-2 px-3 border border-border rounded-[4px]" data-testid="new-procedure-form">
              <input
                data-testid="new-procedure-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Procedure name"
                className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
                autoFocus
              />
              <input
                data-testid="new-procedure-description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Description (optional)"
                className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
              />
              <div className="flex gap-2">
                <button
                  data-testid="new-procedure-cancel"
                  onClick={() => {
                    setAdding(false)
                    setNewName('')
                    setNewDescription('')
                  }}
                  className="inline-flex items-center h-7 px-3 rounded-[4px] border border-border text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  data-testid="new-procedure-save"
                  onClick={handleSaveNew}
                  className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
                >
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
